import { Box, Button, Rating, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Dialog } from "@mui/material";
import { useEffect, useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { EvaluationSummary } from "../components/EvaluationSummary";
import { EvaluationActivity } from "../components/EvaluationActivity";
import { getScheduler, getCalendlyMeetingDetails, getAdvisorMeetings, getAdvisorMeeting, advisorRateMeeting } from "../../../core/services/gutcheck-api.service";
import { DocumentTitle } from "../../../components/DocumentTitle";
import useAnalytics from "../../../hooks/useAnalytics";
import { EventBusy, Edit, StarBorder, Star, DoNotDisturb } from "@mui/icons-material";
import { AdvisorTable } from "../../company/styles/AdvisorStyles";
import { DateDisplay } from "../../meeting";
import { ProjectLogo } from "../../firm/components/ProjectLogo";
import { CompanyLogo } from "../../company/components/CompanyLogo";
import { CalendlyScheduler, MeetingLinks } from "../../meeting/components/CalendlyScheduler";
import { RATING_LABELS } from "../../../constants/ratings";

const RatingDialog = ({ meeting, advisor, onRate, onClose }: any) => {
    const [rating, setRating] = useState<number | null>(meeting.ratings?.[0]?.rating || null);
    const [hoveredRating, setHoveredRating] = useState<number | null>(null);

    const handleRatingChange = (_event: any, newValue: number | null) => {
        setRating(newValue);
        if (newValue !== null) {
            onRate(_event, meeting.uuid, newValue);
        }
    };

    return (
        <Dialog
            open={true}
            onClose={onClose}
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: '550px',
                    maxWidth: '550px'
                }
            }}
        >
            <Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>

                        <h5>Please rate your meeting with {advisor.survey_link.project.name}</h5>
                        <p>We hope you had a great meeting with {advisor.survey_link.project.name}. Please rate this meeting so that we can steer more of the right introductions your way.</p>
                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center', backgroundColor: "#ffffff", borderRadius: "20px", padding: "20px", border: "1px solid #c9c9c9" }}>
                            <CompanyLogo project={advisor.survey_link.project} size="large" />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: "40px", width: '100%' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <DoNotDisturb
                                            onClick={() => {
                                                onRate(meeting.uuid, 0);
                                                onClose();
                                            }}
                                            onMouseOver={() => setHoveredRating(0)}
                                            onMouseLeave={() => setHoveredRating(-1)}
                                            sx={{
                                                cursor: 'pointer',
                                                width: '50px',
                                                height: '50px',
                                                paddingBottom: '5px',
                                                color: rating === 0 ? 'error.main' : 'action.disabled',
                                                '&:hover': { color: 'error.main' }
                                            }}
                                        />
                                        <Rating
                                            value={rating}
                                            max={4}
                                            size="large"
                                            icon={<Star sx={{ width: '50px', height: '50px' }} />}
                                            emptyIcon={<StarBorder sx={{ width: '50px', height: '50px' }} />}
                                            onChange={handleRatingChange}
                                            onChangeActive={(_event, newHover) => {
                                                setHoveredRating(newHover);
                                            }}
                                        />
                                    </Box>
                                    <Typography>
                                        {hoveredRating !== null && hoveredRating !== -1
                                            ? RATING_LABELS[hoveredRating]
                                            : rating !== null
                                                ? RATING_LABELS[rating]
                                                : "Please rate the meeting"}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Dialog >
    );
};

export const EvaluationEngage = () => {
    const { advisor, investmentUnlocked, loading, loadAdvisor } = useOutletContext<any>();
    const { trackAdvisorPage, trackAdvisorEvent } = useAnalytics();
    const [isDemoSchedulerOpen, setIsDemoSchedulerOpen] = useState(false); // State for DemoScheduler
    const [scheduler, setScheduler] = useState<any | null>(null);
    const [meetingDetails, setMeetingDetails] = useState<any>(null);
    const [meetings, setMeetings] = useState<any>([]);
    const [selectedMeeting, setSelectedMeeting] = useState<any>(null);

    const { meetingUuid } = useParams();

    useEffect(() => {
        if (meetingUuid && meetings) {
            setSelectedMeeting(meetings.find((meeting: any) => meeting.uuid === meetingUuid));
            // remove uuid from url
            window.location.pathname.replace(`/expert/${advisor.uuid}/engage/${meetingUuid}`, `/expert/${advisor.uuid}/engage`);
        }
    }, [meetingUuid, meetings]);

    useEffect(() => {
        if (advisor) {
            trackAdvisorPage(advisor, 'expert flow - engage');
        }
    }, [advisor]);

    useEffect(() => {
        if (window.location.hash === "#calendar" && !advisor.prospect_scheduled) {
            setIsDemoSchedulerOpen(true);
            trackAdvisorEvent(advisor, 'Viewed calendar');
        }
    }, []);

    useEffect(() => {
        // Fetch the scheduler data
        fetchScheduler();

        return () => {
            setScheduler(null);
        }
    }, []);

    const fetchMeetingDetails = async () => {
        if (advisor?.prospect_event_invitee_uri) {
            const inviteeId = advisor.prospect_event_invitee_uri.split("/invitees/")[1];
            const { data, error } = await getCalendlyMeetingDetails(advisor.public_id, inviteeId);
            if (data) {
                if (data.invitee.canceled && !data.invitee.rescheduled) {
                    setMeetingDetails(null);
                    loadAdvisor();
                } else {
                    setMeetingDetails(data.invitee);
                }
            } else {
                console.error('Error fetching meeting details:', error);
            }
        }
    };

    const handleMeetingEvent = async (e: any, meetingUuid: string) => {
        const { data, error } = await getAdvisorMeeting(advisor.public_id, meetingUuid);
        if (data) {
            const meeting = data.meeting;
            setMeetings(meetings.map((m: any) => m.id === meeting.id ? meeting : m));
        }
    }

    const fetchMeetings = async () => {
        const { data, error } = await getAdvisorMeetings(advisor.public_id);
        if (data) {
            setMeetings(data.meetings);
            setIsDemoSchedulerOpen(data.meetings.filter((meeting: any) => meeting.status === "scheduled").length === 0);
        }
    }

    useEffect(() => {
        fetchMeetingDetails();
        fetchMeetings();
    }, [advisor?.prospect_event_invitee_uri]);

    const handleCloseDemoScheduler = () => {
        setIsDemoSchedulerOpen(false);
        fetchMeetingDetails();
    }

    const handleRating = async (e: any, meetingUuid: string, rating: number | null) => {
        if (rating === null) {
            return;
        }

        const { data, error } = await advisorRateMeeting(advisor.public_id, meetingUuid, rating);
        if (data) {
            fetchMeetings();
        }
    }


    const fetchScheduler = async () => {
        const { data, error } = await getScheduler(advisor.public_id, "intro");
        if (error) {
            console.error("Error fetching scheduler:", error);
        } else {
            setScheduler(data.scheduler);
        }
    };

    return (
        <>
            <DocumentTitle pageTitle={`Engage with a ${advisor?.survey_link?.project?.public_title}`} />
            <div className="flex-column left-column">
                <div className="evaluation-content" id="evaluation-content">
                    <h5>Engage with the {advisor.survey_link.project.name} team</h5>
                    <p>Schedule a meeting with the {advisor.survey_link.project.name} team to explore a trial or a partnership.</p>

                    {meetings?.length > 0 && (
                        <TableContainer>
                            <AdvisorTable>
                                <TableHead>
                                    <TableRow>
                                        <TableCell className="center">
                                            Date
                                        </TableCell>
                                        <TableCell>
                                            Time
                                        </TableCell>
                                        <TableCell>
                                            Description
                                        </TableCell>
                                        <TableCell>
                                            Status
                                        </TableCell>
                                        <TableCell className="center">
                                            Action
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {meetings?.map((meeting: any) => (
                                        <TableRow key={meeting.uuid}>
                                            <TableCell className="center">
                                                <DateDisplay meeting={meeting} />
                                            </TableCell>
                                            <TableCell>
                                                {new Date(meeting.event_start).toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' })}
                                            </TableCell>
                                            <TableCell>
                                                {meeting.description}
                                            </TableCell>

                                            <TableCell>
                                                {meeting.status.charAt(0).toUpperCase() + meeting.status.slice(1).toLowerCase()}
                                            </TableCell>
                                            <TableCell className="center">
                                                {(meeting.status === "completed" || new Date(meeting.event_start) < new Date()) && (
                                                    <Box sx={{
                                                        minHeight: '60px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        cursor: 'pointer',
                                                        flexDirection: 'column',
                                                        gap: '5px'
                                                    }}
                                                        onClick={() => setSelectedMeeting(meeting)}>
                                                        <Rating
                                                            value={meeting.ratings?.length > 0 ? meeting.ratings[0].rating : null}
                                                            max={4}
                                                            readOnly
                                                        />
                                                        <Typography component="legend" variant="body2">
                                                            {meeting.ratings?.length > 0 ? (
                                                                RATING_LABELS[meeting.ratings[0].rating]
                                                            ) : "Please rate the meeting"}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {(meeting.status === "scheduled" && new Date(meeting.event_start) > new Date()) && <MeetingLinks advisor={advisor} meeting={meeting} handleMeetingEvent={handleMeetingEvent} fetchMeetingDetails={fetchMeetingDetails} />}
                                                {meeting.status === "requested" && <button onClick={() => setIsDemoSchedulerOpen(true)}>Schedule a Demo</button>}

                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </AdvisorTable>
                        </TableContainer>
                    )}

                    {isDemoSchedulerOpen && scheduler && (
                        <CalendlyScheduler project={advisor.survey_link.project} scheduler={scheduler} advisor={advisor} surveyLink={advisor.survey_link} onMeetingCreated={() => { }} />

                    )}
                    {!isDemoSchedulerOpen && scheduler && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                            <Button variant="contained" onClick={() => setIsDemoSchedulerOpen(true)}>Schedule another Demo</Button>
                        </Box>
                    )}
                </div>
            </div>
            <div className="flex-column right-column">
                <EvaluationSummary advisor={advisor} investmentUnlocked={investmentUnlocked} />
                <EvaluationActivity advisor={advisor} />
            </div>
            {selectedMeeting && (
                <RatingDialog
                    meeting={selectedMeeting}
                    advisor={advisor}
                    onRate={handleRating}
                    onClose={() => setSelectedMeeting(null)}
                />
            )}
        </>
    );
}